import { userFriendlyError } from "../utils/userFriendlyError";

const isBrowser = typeof window !== "undefined";
const isProduction = process.env.NODE_ENV === "production";

const API_URL = process.env.REACT_APP_API_URL || "https://forms.eupago.pt";
const FRONT_URL = process.env.REACT_APP_FRONT_URL
  || (isBrowser ? window.location.origin : "https://formularios.eupago.pt");

const isLocalAddress = (value: string) => /(^|\/\/)(localhost|127\.0\.0\.1)(:|\/|$)/i.test(value);

if (isProduction && (isLocalAddress(API_URL) || isLocalAddress(FRONT_URL))) {
  throw new Error("Production builds cannot use localhost frontend or API URLs.");
}

export const URL = API_URL;
export const URL_FRONT = FRONT_URL;

/**
 * Local form images are served by the configured API. The backend may advertise
 * its ngrok URL for webhooks, but ngrok's browser warning is not a valid image.
 * S3 presigned URLs are left untouched.
 */
export const resolveMediaUrl = (value: string): string => {
  if (!value) return value;

  // Legacy catalogue images were exposed outside /api and were therefore
  // swallowed by the SPA reverse proxy in staging. Keep old database values
  // working while all new uploads use the public API route.
  const legacyCatalogImage = value.match(/\/catalogProductImage\/([^/]+)\/([^?#]+)(?:[?#].*)?$/);
  if (legacyCatalogImage) {
    return `${API_URL.replace(/\/$/, "")}/api/public/commerce/product-images/${legacyCatalogImage[1]}/${legacyCatalogImage[2]}`;
  }

  const localImagePath = value.match(/\/(?:formImage|productImage|api\/public\/commerce\/product-images)\/[^?#]+(?:[?#].*)?$/)?.[0];
  return localImagePath
    ? `${API_URL.replace(/\/$/, "")}${localImagePath}`
    : value;
};

type SuccessCallback<T = unknown> = (response: T) => void;
type FailureCallback = (error: Error) => void;

function buildHeaders(request: RequestInit, withToken: boolean): Headers {
  const headers = new Headers(request.headers || undefined);

  if (withToken) {
    const token = sessionStorage.getItem("jwt");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  if (!(request.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  } else {
    headers.delete("Content-Type");
  }

  headers.set("Accept", "application/json");
  return headers;
}

async function parseResponse(res: Response, rawResponse = false): Promise<unknown> {
  if (!res.ok) {
    const text = await res.text();
    const statusFallback = res.status >= 500
      ? "O serviço está temporariamente indisponível. Tente novamente dentro de instantes."
      : "Não foi possível concluir o pedido. Confirme os dados e tente novamente.";
    throw new Error(userFriendlyError(text, statusFallback));
  }

  if (rawResponse) {
    return res;
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }

  return res.text();
}

function executeRequest<T = unknown>(
  url: string,
  request: RequestInit,
  success: SuccessCallback<T>,
  failure: FailureCallback,
  withToken: boolean,
  rawResponse = false,
): Promise<T> {
  const finalRequest: RequestInit = {
    ...request,
    headers: buildHeaders(request, withToken),
  };

  return fetch(url, finalRequest)
    .then((res) => parseResponse(res, rawResponse))
    .then((res) => {
      success(res as T);
      return res as T;
    })
    .catch((err) => {
      const error = new Error(userFriendlyError(err));
      failure(error);
      throw error;
    });
}

export function makeHttpRequest<T = unknown>(url: string, request: RequestInit, success: SuccessCallback<T>, failure: FailureCallback): Promise<T> {
  return executeRequest(url, request, success, failure, true);
}

export function makeHttpRequestWithoutToken<T = unknown>(url: string, request: RequestInit, success: SuccessCallback<T>, failure: FailureCallback): Promise<T> {
  return executeRequest(url, request, success, failure, false);
}

export function makeHttpRequestNoLogin<T = unknown>(url: string, request: RequestInit, success: SuccessCallback<T>, failure: FailureCallback): Promise<T> {
  return executeRequest(url, request, success, failure, false);
}

export function makeHttpRequestPassword<T = Response>(url: string, request: RequestInit, success: SuccessCallback<T>, failure: FailureCallback): Promise<T> {
  return executeRequest(url, request, success, failure, true, true);
}
